import { useEffect } from 'react';
import { TASK_LIVE_FORBIDDEN, type TaskLiveMessage } from '@shared/types/tasks';
import { useAuthStore } from '@/stores/authStore';
import { whenSessionReady } from '@/lib/session-ready';
import { useTaskStore } from '@/stores/taskStore';
import { usePermission } from '@/hooks/usePermission';
import { useCurrentProject } from '@/lib/current-project';
import { getTaskNotifications } from '@/lib/tasks-api';
import { receiveEditors, setEditingSender } from '@/stores/taskEditingStore';

const PING_MS = 30_000;
const MAX_BACKOFF_MS = 30_000;

function liveUrl(token: string, projectId: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/tasks/live?token=${encodeURIComponent(token)}&project=${encodeURIComponent(projectId)}`;
}

/**
 * One websocket per tab to the current project's live hub (`?project=`), while the tab is in a
 * project; moving to another project closes it and opens that one's. Mounted once in the app layout; it bumps ticks
 * in the task store (the hooks that care refetch). While it's down, `LiveUpdatesIndicator` says so.
 * A hidden tab doesn't reconnect: each try reads the database, so a tab left open overnight would keep
 * it awake. It reconnects as soon as the tab is shown. A refused socket (TASK_LIVE_FORBIDDEN)
 * isn't retried at all.
 */
export function TaskLiveConnector() {
  const canView = usePermission('PROJECT_VIEW');
  const projectId = useCurrentProject()?.id;

  useEffect(() => {
    if (!canView || !projectId) return undefined;
    const store = useTaskStore.getState();
    let socket: WebSocket | null = null;
    let ping: ReturnType<typeof setInterval> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let stopped = false;
    let hasConnected = false;
    /** The socket dropped while the tab was hidden: reconnect when it's shown. */
    let waitingForVisible = false;

    const connect = async () => {
      // Opened before the page-load session check answered (T-68): wait for the token.
      if ((await whenSessionReady()) !== 'send' || stopped) return;
      const token = useAuthStore.getState().user?.token;
      if (!token || stopped) return;
      socket = new WebSocket(liveUrl(token, projectId));
      socket.onopen = () => {
        // A reconnect may have missed changes, and so may a first connect after failed tries (the
        // page loaded over HTTP meanwhile); a first connect at once loads fresh anyway.
        const mayHaveMissed = hasConnected || attempts > 0;
        attempts = 0;
        store.setConnected(true);
        if (mayHaveMissed) {
          store.resync();
        }
        hasConnected = true;
        // Says what this tab is editing (T-46) and hears who else is.
        const open = socket;
        setEditingSender((text) => open?.readyState === WebSocket.OPEN && open.send(text));
        ping = setInterval(() => socket?.readyState === WebSocket.OPEN && socket.send('ping'), PING_MS);
      };
      socket.onmessage = (event) => {
        if (event.data === 'pong') return;
        let message: TaskLiveMessage;
        try {
          message = JSON.parse(String(event.data)) as TaskLiveMessage;
        } catch {
          return; // Not one of ours.
        }
        if (message.type === 'editors') {
          receiveEditors(message.editors);
          return;
        }
        store.receive(message);
      };
      socket.onclose = (event) => {
        clearInterval(ping);
        // A socket closed on purpose (another project, sign-out) can close after the next one opened:
        // it mustn't mark that one down.
        if (stopped) return;
        setEditingSender(null);
        if (event.code === TASK_LIVE_FORBIDDEN) {
          // No task access any more (the next token renewal hides tasks too): not an outage.
          stopped = true;
          store.setConnected(false, { idle: true });
          return;
        }
        store.setConnected(false);
        attempts += 1;
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(attempts, 5));
        retry = setTimeout(reconnect, delay);
      };
    };

    // An expired token closes the socket; any API call refreshes it before we retry.
    const reconnect = () => {
      if (stopped) return;
      if (document.hidden) {
        waitingForVisible = true;
        return;
      }
      void getTaskNotifications().finally(() => void connect());
    };
    const onVisibility = () => {
      if (document.hidden || !waitingForVisible) return;
      waitingForVisible = false;
      clearTimeout(retry);
      reconnect();
    };

    document.addEventListener('visibilitychange', onVisibility);
    void connect();
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(ping);
      clearTimeout(retry);
      setEditingSender(null);
      socket?.close();
      // Closing on purpose (sign-out, another project): not an outage to report.
      store.setConnected(false, { idle: true });
    };
  }, [canView, projectId]);

  return null;
}
